/**
 * Pure view-model helpers for the avatar org switcher (VMS-72).
 *
 * `buildOrgMenu` turns a /me response + the active org id into the two lists
 * the dropdown renders: active orgs (sorted, current flagged) and pending
 * invites. No React / DOM here so it unit-tests in the node env like
 * decideLogin() does.
 */

/** An org as /me lists it. `role` is shown verbatim, so it stays a label. */
interface OrgMenuOrg {
    orgId: string;
    orgName?: string;
    role?: string;
}

interface OrgMenuInvite {
    orgId: string;
    orgName?: string;
    invitedBy?: string;
}

/** The parts of a /me answer the menu reads. */
interface OrgMenuSource {
    orgs?: readonly OrgMenuOrg[];
    pendingInvites?: readonly OrgMenuInvite[];
}

interface OrgMenuEntry {
    orgId: string;
    orgName: string;
    role: string;
    isCurrent: boolean;
}

interface OrgMenuInviteEntry {
    orgId: string;
    orgName: string;
    invitedBy: string;
}

interface OrgMenu {
    active: OrgMenuEntry[];
    invites: OrgMenuInviteEntry[];
}

function getName(o: OrgMenuOrg | OrgMenuInvite): string {
    return o.orgName || o.orgId || '';
}

export function buildOrgMenu(me: OrgMenuSource, activeOrgId: string | null): OrgMenu {
    const orgs = Array.isArray(me?.orgs) ? me.orgs : [];
    const invites = Array.isArray(me?.pendingInvites) ? me.pendingInvites : [];

    const activeOrgMenuEntries = orgs
        .map((o): OrgMenuEntry => ({
            orgId: o.orgId,
            orgName: getName(o),
            role: o.role || '',
            isCurrent: o.orgId === activeOrgId,
        }))
        .sort((a, b) => a.orgName.localeCompare(b.orgName));

    return {
        active: activeOrgMenuEntries,
        invites: invites.map((i): OrgMenuInviteEntry => ({
            orgId: i.orgId,
            orgName: getName(i),
            invitedBy: i.invitedBy || '',
        })),
    };
}

export function getCurrentOrgName(
    orgs: readonly OrgMenuOrg[], activeOrgId: string | null,
): string {
    const match = (Array.isArray(orgs) ? orgs : []).find((o) => o.orgId === activeOrgId);
    return match ? getName(match) : '';
}
